import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Download, Search, X } from "lucide-react";
import { Seg, useMarkets } from "./ui";
import { weexOf } from "./LiveDesk";
import { base, count, downloadCsv, fmtTime, money, pct, price, toDate, tone, tradePnl } from "./format";

const PAGE = 50;

const COLS = [
  { key: "symbol", label: "Symbol", get: (r) => base(r.symbol), csv: (r) => base(r.symbol) },
  { key: "side", label: "Side", get: (r) => String(r.direction || "").toUpperCase(), csv: (r) => r.direction },
  { key: "leverage", label: "Lev.", num: true, get: (r) => Number(r.leverage || 0), csv: (r) => r.leverage },
  { key: "account", label: "Account", get: (r) => r.account_id || r.account || "", csv: (r) => r.account_id || r.account },
  { key: "entry", label: "Entry", num: true, get: (r) => Number(r.entry_price || 0), csv: (r) => r.entry_price },
  { key: "mark", label: "Mark / exit", num: true, get: (r) => Number(r.current_price ?? r.exit_price ?? 0), csv: (r) => r.current_price ?? r.exit_price },
  { key: "pnlpct", label: "P&L %", num: true, get: (r) => Number(r.unrealized_pnl_pct ?? r.pnl_pct ?? 0), csv: (r) => r.unrealized_pnl_pct ?? r.pnl_pct },
  { key: "pnl", label: "P&L $", num: true, get: (r) => (r.unrealized_pnl != null ? Number(r.unrealized_pnl) : tradePnl(r)), csv: (r) => (r.unrealized_pnl != null ? r.unrealized_pnl : tradePnl(r)) },
  { key: "weex", label: "WEEX", get: (r) => (r.weex_validation ? weexOf(r).status : ""), csv: (r) => (r.weex_validation ? weexOf(r).status : "") },
  { key: "time", label: "Opened / closed", num: true, get: (r) => toDate(r.closed_at || r.exit_time || r.opened_at || r.entry_time)?.getTime() || 0,
    csv: (r) => (toDate(r.closed_at || r.exit_time || r.opened_at || r.entry_time)?.toISOString() || "") },
  { key: "reason", label: "Close reason", get: (r) => String(r.close_reason || r.exit_reason || "").replace(/_/g, " "), csv: (r) => r.close_reason || r.exit_reason },
];

export default function TradeLedger({ openRows, closedRows, accountFilter, onAccountFilter, onOpenRow }) {
  const { tz } = useMarkets();
  const [view, setView] = useState("open");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "time", dir: -1 });
  const [page, setPage] = useState(0);

  const rows = view === "open" ? openRows : closedRows;
  const accountOptions = useMemo(() => {
    const set = new Set([...openRows, ...closedRows].map((r) => r.account_id || r.account).filter(Boolean));
    return ["ALL", ...[...set].sort()];
  }, [openRows, closedRows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = rows.filter((r) => accountFilter === "ALL" || (r.account_id || r.account) === accountFilter);
    if (needle) {
      list = list.filter((r) => [r.symbol, r.direction, r.strategy || r.engine, r.close_reason || r.exit_reason, r.account_id || r.account]
        .filter(Boolean).join(" ").toLowerCase().includes(needle));
    }
    const col = COLS.find((c) => c.key === sort.key) || COLS[9];
    return [...list].sort((a, b) => {
      const x = col.get(a), y = col.get(b);
      return (col.num ? x - y : String(x).localeCompare(String(y))) * sort.dir;
    });
  }, [rows, q, accountFilter, sort]);

  useEffect(() => { setPage(0); }, [view, q, accountFilter, sort]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const shown = filtered.slice(page * PAGE, page * PAGE + PAGE);
  const retired = view === "closed" && closedRows.some((r) => r.archived);

  const head = (c) => {
    const on = sort.key === c.key;
    return (
      <th key={c.key} className={c.num ? "r" : ""} aria-sort={on ? (sort.dir > 0 ? "ascending" : "descending") : "none"}>
        <button type="button" onClick={() => setSort((s) => ({ key: c.key, dir: s.key === c.key ? -s.dir : (c.num ? -1 : 1) }))}>
          {c.label}{on ? (sort.dir > 0 ? <ChevronUp aria-hidden /> : <ChevronDown aria-hidden />) : null}
        </button>
      </th>
    );
  };

  return (
    <section className="mk-card mk-ledger">
      <header className="mk-ledger-bar">
        <Seg label="Ledger view" value={view} onChange={setView}
          options={[{ value: "open", label: `Open (${count(openRows.length)})` }, { value: "closed", label: `Closed (${count(closedRows.length)})` }]} />
        <label className="mk-search">
          <Search aria-hidden />
          <span className="mk-sr">Search trades</span>
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Symbol, side, engine, reason, account" />
          {q ? <button type="button" onClick={() => setQ("")} aria-label="Clear search"><X aria-hidden /></button> : null}
        </label>
        <label className="mk-select">
          <span>Account</span>
          <select value={accountFilter} onChange={(e) => onAccountFilter(e.target.value)}>
            {accountOptions.map((a) => <option key={a} value={a}>{a === "ALL" ? "All accounts" : a}</option>)}
          </select>
        </label>
        <button type="button" className="mk-btn" disabled={!filtered.length}
          onClick={() => downloadCsv(`aeon-${view}-trades-${new Date().toISOString().slice(0, 10)}.csv`,
            COLS.map((c) => ({ label: c.label, csv: c.csv })), filtered)}>
          <Download aria-hidden /> Export CSV
        </button>
      </header>
      <p className="mk-scope">
        {view === "open" ? "Live OKX marks · P&L is unrealized" : `Realized P&L per trade${retired ? " · includes retired accounts (PRO, STARTER, TIER_*)" : ""}`}
        {" "}· times in {fmtTime(new Date(), tz).split(" ").pop()} · {count(filtered.length)} rows · click a row for details
      </p>
      {view === "closed" && filtered.length ? (() => {
        const byPnl = [...filtered].sort((x, y) => tradePnl(y) - tradePnl(x));
        const best = byPnl[0], worst = byPnl[byPnl.length - 1];
        return (
          <div className="mk-bestworst">
            <button type="button" className="mk-btn" onClick={() => onOpenRow(best, false)}>
              Best <b className={`mk-num ${tone(tradePnl(best))}`}>{money(tradePnl(best))}</b> {base(best.symbol)} · {best.account_id || best.account}
            </button>
            <button type="button" className="mk-btn" onClick={() => onOpenRow(worst, false)}>
              Worst <b className={`mk-num ${tone(tradePnl(worst))}`}>{money(tradePnl(worst))}</b> {base(worst.symbol)} · {worst.account_id || worst.account}
            </button>
          </div>
        );
      })() : null}
      <div className="mk-table-wrap">
        <table className="mk-table mk-ledger-table">
          <thead><tr>{COLS.map(head)}</tr></thead>
          <tbody>
            {shown.map((r, i) => {
              const pnl = COLS[7].get(r);
              const side = String(r.direction || "").toUpperCase();
              const w = r.weex_validation ? weexOf(r) : null;
              return (
                <tr key={r.id || r._id || `${r.symbol}-${i}`} className="is-click" tabIndex={0}
                  onClick={() => onOpenRow(r, view === "open")}
                  onKeyDown={(e) => { if (e.key === "Enter") onOpenRow(r, view === "open"); }}>
                  <td><b>{base(r.symbol)}</b></td>
                  <td className={side === "LONG" ? "is-up" : "is-down"}>{side}</td>
                  <td className="r mk-num">{Number(r.leverage || 0)}x</td>
                  <td>{r.account_id || r.account || "—"}{r.archived ? <span className="mk-tag">retired</span> : null}</td>
                  <td className="r mk-num">{price(r.entry_price)}</td>
                  <td className="r mk-num">{price(r.current_price ?? r.exit_price)}</td>
                  <td className={`r mk-num ${tone(pnl)}`}>{pct(r.unrealized_pnl_pct ?? r.pnl_pct, { sign: true })}</td>
                  <td className={`r mk-num ${tone(pnl)}`}>{money(pnl)}</td>
                  <td>{w ? <span className="mk-weex"><i className="mk-dot" style={{ background: w.color }} /> <span title={w.title}>{w.status.replace(/_/g, " ").toLowerCase()}</span></span> : "—"}</td>
                  <td className="mk-num">{fmtTime(r.closed_at || r.exit_time || r.opened_at || r.entry_time, tz, { date: true })}</td>
                  <td className="mk-trunc">{String(r.close_reason || r.exit_reason || (view === "open" ? "open" : "—")).replace(/_/g, " ")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!shown.length ? <p className="mk-empty">No trades match this view. Clear the search or account filter.</p> : null}
      </div>
      {pages > 1 ? (
        <nav className="mk-pager" aria-label="Ledger pages">
          <button type="button" className="mk-btn" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</button>
          <span className="mk-num">Page {page + 1} of {pages}</span>
          <button type="button" className="mk-btn" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>Next</button>
        </nav>
      ) : null}
    </section>
  );
}
